/**
 * Tenant → Portfolio, the landing page — REAL for the first time.
 *
 * The static overview was hardcoded demo content ("6 properties · 412 units")
 * that outlived its prototype purpose and would have lied to the first real
 * customer. Everything here comes from the API; where a number does not exist
 * yet (certifications), the tile says so instead of inventing one.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { request, type Property } from '../api';
import { useSession } from '../session';

export function Overview() {
  const { me } = useSession();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    request<{ properties: Property[] }>('/properties')
      .then((d) => setProperties(d.properties))
      .catch(() => setFailed(true));
  }, []);

  const list = properties ?? [];
  const units = list.reduce((n, p) => n + p.unitCount, 0);

  return (
    <>
      <div className="page__head">
        <div>
          <h1>Portfolio</h1>
          <p className="sub">{me.company.name}</p>
        </div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 14 }}>
        <div className="kpi">
          <b>{properties ? list.length : '–'}</b>
          <span>Properties</span>
        </div>
        <div className="kpi">
          <b>{properties ? units : '–'}</b>
          <span>Units</span>
        </div>
        <div className="kpi">
          <b>{me.company.userCount}</b>
          <span>Team members</span>
        </div>
        <div className="kpi">
          <b>—</b>
          <span>Certifications · coming soon</span>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2>Properties</h2>
          <div className="spacer" />
          <Link className="btn btn--sm" to="/properties">
            Manage properties
          </Link>
        </div>
        <div className="panel__body">
          {failed && <p className="dim">Could not load properties.</p>}
          {properties && list.length === 0 && (
            <p className="dim" style={{ margin: 0 }}>
              No properties yet — start on the <Link to="/properties">Properties page</Link>.
            </p>
          )}
          {list.length > 0 && (
            <div className="props">
              {list.map((p) => (
                <a
                  key={p.id}
                  className="prop"
                  href={`/properties/${p.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/properties/${p.id}`);
                  }}
                >
                  <div className="prop__top">
                    <div>
                      <h3>{p.name}</h3>
                      <span className="addr">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</span>
                    </div>
                    <span className="prop__badge">
                      <span className={`tag tag--${p.status === 'active' ? 'ok' : 'grey'}`}>{p.status}</span>
                    </span>
                  </div>
                  {p.fundingTypes.length > 0 && (
                    <div className="prop__progs">
                      {p.fundingTypes.map((f) => (
                        <span key={f} className="tag tag--info">
                          {f.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="prop__stats">
                    <div>
                      <b>{p.unitCount}</b>
                      <span>Units</span>
                    </div>
                    <div>
                      <b>{p.county ?? '—'}</b>
                      <span>County</span>
                    </div>
                    <div>
                      <b>{p.stateProjectNumber ? '✓' : '—'}</b>
                      <span>State #</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
